import type { CSSProperties } from 'react';
import { ICON_PATHS, type IconName } from './icon-paths.generated';

export type { IconName };

/** A name from data (a Marketplace entry, TF-171) as an icon the app bundles; an unknown one draws a puzzle piece. */
export const iconOf = (name: string): IconName => (name in ICON_PATHS ? (name as IconName) : 'extension');
/** 18 inside a button with a label, 20 in dense tables and chips, 24 standing alone, 40 or 48 large. */
export type IconSize = 18 | 20 | 24 | 40 | 48;

/**
 * The one way the app draws an icon (TF-97): a Material Symbol, Outlined, weight 400, bundled as an
 * SVG path (only the icons listed in icon-names.ts; nothing is fetched from Google). The drawing at
 * optical size 20 is used up to 20px and the one at 24 above, as the variable font would. `filled`
 * marks a selected state (the current destination, a pin) for the icons in FILLED_ICONS.
 *
 * Decorative by default: hidden from screen readers, since the control around it carries the name.
 * Give `label` only to an icon that means something on its own.
 */
export function Icon({ name, size = 24, filled = false, label, className, style }: { name: IconName; size?: IconSize; filled?: boolean; label?: string; className?: string; style?: CSSProperties }) {
  const [small, regular, fill] = ICON_PATHS[name];
  const d = filled && fill ? fill : size <= 20 ? small : regular;
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 -960 960 960"
      fill="currentColor"
      focusable="false"
      style={style}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      <path d={d} />
    </svg>
  );
}
